import { Button, Checkbox } from '@roger/ui-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, RotateCw, Trash2 } from 'lucide-react';
import { Page } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { BoardPage } from './pageBoard';

interface SortablePageProps {
  page: BoardPage;
  position: number;
  pdf: PDFDocumentProxy | null;
  disabled: boolean;
  onRotate: () => void;
  onRemove: () => void;
  /** Whether this is the page the large preview is showing. */
  selected?: boolean;
  /** Show this page in the large preview. Omitted where there is not one. */
  onSelect?: () => void;
  /** Whether this page is ticked for splitting out. */
  checked?: boolean;
  /** Tick or untick it. Omitted where splitting is not offered. */
  onCheckedChange?: () => void;
}

const THUMBNAIL_WIDTH = 96;

/**
 * One page on the board.
 *
 * Draggable because it calls useSortable itself: DraggableGrid supplies the
 * context and the sensors, but an item that does not attach the listeners is
 * simply not draggable, which is silent rather than broken-looking.
 *
 * The rotation is a CSS transform on the thumbnail rather than react-pdf's
 * `rotate` prop, because that prop REPLACES the page's own angle while the
 * server ADDS to it. A fax often arrives already turned, so overriding it made
 * the board show a page lying on its side that was stored upright: somebody
 * would straighten it on screen and file it upside down.
 */
export default function SortablePage({
  page,
  position,
  pdf,
  disabled,
  onRotate,
  onRemove,
  selected = false,
  onSelect,
  checked = false,
  onCheckedChange,
}: SortablePageProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.key, disabled });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded border bg-background p-2 ${isDragging ? 'opacity-50' : ''} ${
        selected ? 'border-primary ring-2 ring-primary' : ''
      }`}
    >
      {/* A button rather than a click handler on the tile, so the page can be
          reached and chosen from the keyboard: the rail is how somebody moves
          through a long fax, and the drag handle beside it is not a substitute
          for being able to tab to a page. */}
      <button
        type='button'
        className='flex h-32 w-full items-center justify-center overflow-hidden'
        aria-label={`Show page ${position}`}
        aria-current={selected}
        onClick={onSelect}
      >
        {pdf ? (
          <div
            style={{ transform: `rotate(${page.rotation}deg)` }}
            className='transition-transform'
          >
            <Page
              pdf={pdf}
              pageNumber={page.page}
              width={THUMBNAIL_WIDTH}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              loading={
                <div className='h-32 w-24 animate-pulse rounded bg-muted' />
              }
              error={
                <div className='flex h-32 w-24 items-center justify-center rounded bg-muted text-xs'>
                  No preview
                </div>
              }
            />
          </div>
        ) : (
          <div className='h-32 w-24 animate-pulse rounded bg-muted' />
        )}
      </button>
      <div className='mt-2 flex items-center justify-between'>
        <button
          type='button'
          className='cursor-grab text-muted-foreground'
          aria-label={`Reorder page ${position}`}
          disabled={disabled}
          {...attributes}
          {...listeners}
        >
          <GripVertical className='h-4 w-4' />
        </button>
        {onCheckedChange && (
          <Checkbox
            aria-label={`Split out page ${position}`}
            checked={checked}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
          />
        )}
        <span className='text-xs text-muted-foreground'>{position}</span>
        <div className='flex gap-1'>
          <Button
            variant='ghost'
            size='sm'
            disabled={disabled}
            aria-label={`Rotate page ${position}`}
            onClick={onRotate}
          >
            <RotateCw className='h-3 w-3' />
          </Button>
          <Button
            variant='ghost'
            size='sm'
            disabled={disabled}
            aria-label={`Remove page ${position}`}
            onClick={onRemove}
          >
            <Trash2 className='h-3 w-3' />
          </Button>
        </div>
      </div>
    </div>
  );
}
